import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import queryString from "query-string";
import axios from "axios";

import {
  SearchFloating,
  // SidebarFilters,
  SortBar,
  PropertyCard,
  Pagination,
  Loader,
  NoResults,
} from "./Compo"; // we'll export them from index in components folder (below)
// import { fetchProperties, fetchFilterData } from "../../api";
import "./PropertyListPage.css";

const DEFAULTS = {
  page: 1,
  perPage: 12,
  sort: "newest",
  view: "grid",
};

const API_BASE_URL = "/api";
// OR try without port if your backend serves both API and frontend:
// const API_BASE_URL = window.location.origin; // This will use the same origin as frontend

// ✅ FIX: Enhanced axios instance with CORS handling
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
  // Add withCredentials if needed for cookies/auth
  // withCredentials: true,
});

// ✅ FIX: Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log(
      `🚀 Making ${config.method?.toUpperCase()} request to:`,
      config.url
    );
    console.log("Full config:", config);
    return config;
  },
  (error) => {
    console.error("❌ Request error:", error);
    return Promise.reject(error);
  }
);

// ✅ FIX: Add response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`✅ Response from ${response.config.url}:`, response.status);
    return response;
  },
  (error) => {
    console.error("❌ Response error:", {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      url: error.config?.url,
    });
    return Promise.reject(error);
  }
);

export default function PropertyListPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialFilters = {
    transactionType: searchParams.get("transactionType") || "",
    location: searchParams.get("location") || "",
    propertyType: searchParams.get("propertyType") || "",
    area: searchParams.get("area") || "",
  };

  const [loading, setLoading] = useState(false);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [properties, setProperties] = useState([]);

  const [filterOptions, setFilterOptions] = useState({
    transactionType: [],
    locations: [],
    propertyTypes: [],
    areas: [],
  });

  // Filter state
  const [filters, setFilters] = useState({
    ...initialFilters,
    category: "All",
    bedsMin: "",
    bedsMax: "",
    bathsMin: "",
    bathsMax: "",
    priceMin: "",
    priceMax: "",
    rentalPeriod: "Any",
    activeRoomFilter: "All rooms",
  });

  const [sortOption, setSortOption] = useState("Relevance");

  const [meta, setMeta] = useState({ total: 0, page: 1, perPage: 12 });
  //   const [mapOpen, setMapOpen] = useState(false);

  // parse params from URL
  const params = useMemo(
    () => ({
      ...DEFAULTS,
      ...queryString.parse(window.location.search),
      page: Number(searchParams.get("page") || DEFAULTS.page),
      perPage: Number(searchParams.get("perPage") || DEFAULTS.perPage),
    }),
    [searchParams]
  );

  const applyFilters = (newParams) => {
    const merged = { ...params, ...newParams, page: 1 }; // reset page on filter change
    setSearchParams(merged);
    // navigate with new query string (keeps SEO-friendly URL)
    navigate(`${window.location.pathname}?${queryString.stringify(merged)}`);
  };

  const changePage = (page) => {
    const merged = { ...params, page };
    setSearchParams(merged);
    navigate(`${window.location.pathname}?${queryString.stringify(merged)}`);
  };

  // Fetch data on component mount and when URL params change
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Construct API query based on URL parameters
        const queryParams = {
          transactionType: initialFilters.transactionType,
          location: initialFilters.location,
          propertyType: initialFilters.propertyType,
          area: initialFilters.area,
        };

        // Remove empty parameters
        Object.keys(queryParams).forEach((key) => {
          if (!queryParams[key]) delete queryParams[key];
        });

        const [propertiesRes, filterOptionsRes] = await Promise.all([
          // ✅ CHANGED: Use relative URL instead of localhost
          api.get("/properties", {
            params: queryParams,
          }),
          // OLD: axios.get("http://localhost:3000/properties", { params: queryParams })

          // ✅ CHANGED: Use relative URL instead of localhost
          api.get("/filter-options"),
          // OLD: axios.get("http://localhost:3000/filter-options"),
        ]);

        setProperties(propertiesRes.data.data);
        setFilteredProperties(propertiesRes.data.data);
        setFilterOptions(filterOptionsRes.data.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [location.search]);

  // Apply filters and sorting whenever filters or sort option changes
  useEffect(() => {
    if (properties.length === 0) return;

    let filtered = [...properties];

    // Apply basic filters from search params
    if (filters.transactionType) {
      filtered = filtered.filter(
        (p) =>
          p.transactionType.toLowerCase() ===
          filters.transactionType.toLowerCase()
      );
    }
    if (filters.location) {
      filtered = filtered.filter((p) =>
        p.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }
    if (filters.propertyType) {
      filtered = filtered.filter((p) =>
        p.propertyType
          .toLowerCase()
          .includes(filters.propertyType.toLowerCase())
      );
    }
    if (filters.area) {
      filtered = filtered.filter((p) =>
        p.area.toLowerCase().includes(filters.area.toLowerCase())
      );
    }

    // Apply additional filters
    if (filters.category !== "All") {
      filtered = filtered.filter((p) => {
        switch (filters.category) {
          case "Apartments":
            return p.propertyType === "Apartment";
          case "Houses":
            return p.propertyType === "House";
          case "Rooms":
            return p.propertyType === "Room";
          case "Townhouses":
            return p.propertyType === "Townhouse";
          default:
            return true;
        }
      });
    }

    if (filters.bedsMin)
      filtered = filtered.filter((p) => p.beds >= parseInt(filters.bedsMin));
    if (filters.bedsMax)
      filtered = filtered.filter((p) => p.beds <= parseInt(filters.bedsMax));
    if (filters.bathsMin)
      filtered = filtered.filter((p) => p.baths >= parseInt(filters.bathsMin));
    if (filters.bathsMax)
      filtered = filtered.filter((p) => p.baths <= parseInt(filters.bathsMax));
    if (filters.priceMin)
      filtered = filtered.filter((p) => p.price >= parseInt(filters.priceMin));
    if (filters.priceMax)
      filtered = filtered.filter((p) => p.price <= parseInt(filters.priceMax));

    if (filters.rentalPeriod !== "Any") {
      filtered = filtered.filter((p) => {
        if (!p.rentalPeriod) return false;
        switch (filters.rentalPeriod) {
          case "1 - 12 months":
            return p.rentalPeriod.includes("12");
          case "13 - 24 months":
            return p.rentalPeriod.includes("24");
          case "24+ months":
            return p.rentalPeriod.includes("24");
          default:
            return true;
        }
      });
    }

    if (filters.activeRoomFilter !== "All rooms") {
      const bedsFilter = parseInt(filters.activeRoomFilter.split(" ")[0]);
      filtered = filtered.filter((p) => p.beds === bedsFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortOption) {
        case "Price: Low to High":
          return a.price - b.price;
        case "Price: High to Low":
          return b.price - a.price;
        case "Newest":
          return new Date(b.createdAt) - new Date(a.createdAt);
        default:
          return 0;
      }
    });

    setFilteredProperties(filtered);
  }, [filters, sortOption, properties]);

  // Handler functions
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoomFilterClick = (filter) => {
    setFilters((prev) => ({ ...prev, activeRoomFilter: filter }));
  };

  const handleContactClick = (propertyId) => {
    const property = properties.find((p) => p.id === propertyId);
    navigate("/contactus", {
      state: {
        propertyId,
        from: "search",
        propertyDetails: property,
      },
    });
  };

  // Calculate price range
  const priceRange = filteredProperties.reduce(
    (acc, property) => {
      if (property.price < acc.min) acc.min = property.price;
      if (property.price > acc.max) acc.max = property.price;
      return acc;
    },
    { min: Infinity, max: -Infinity }
  );

  if (loading) {
    return <div className="search-results-page">Loading properties...</div>;
  }

  const handleSearchRedirect = () => {
    navigate("/"); // Redirect to home page
  };

  // Iframe Image Component to bypass CORS
  const IframeImage = ({ src, alt, className }) => {
    const [useFallback, setUseFallback] = useState(false);

    if (useFallback) {
      return (
        <div className="image-fallback">
          <span>Image not available</span>
        </div>
      );
    }

    return (
      <iframe
        src={src}
        title={alt}
        className={`iframe-image ${className}`}
        onError={() => setUseFallback(true)}
        scrolling="no"
        frameBorder="0"
        loading="lazy"
      />
    );
  };

  // Parse image data from property
  const parseImageData = (imageData) => {
    try {
      if (typeof imageData === "string") {
        return JSON.parse(imageData);
      } else if (Array.isArray(imageData)) {
        return imageData;
      }
      return [];
    } catch (error) {
      console.error("Error parsing image data:", error);
      return [];
    }
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 1200,
    autoplay: true,
    autoplaySpeed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    pauseOnHover: false,
  };

  return (
    <div className="plp-root">
      <SearchFloating applyFilters={applyFilters} initial={params} />
      <div className="plp-container">
        {/* <aside className={`plp-sidebar ${mapOpen ? "collapsed" : ""}`}> */}
        {/* {filtersLoading ? (
          <Loader />
        ) : (
          <SidebarFilters
            options={filterOptions}
            initial={params}
            onApply={applyFilters}
          />
        )} */}
        {/* </aside> */}

        <main className="plp-main">
          <div className="plp-top">
            <SortBar
              sort={params.sort}
              view={params.view}
              setView={(v) => applyFilters({ view: v })}
              setSort={(s) => applyFilters({ sort: s })}
              //   toggleMap={() => setMapOpen((v) => !v)}
              //   isMapOpen={mapOpen}
            />
          </div>

          {loading ? (
            <Loader />
          ) : properties.length === 0 ? (
            <NoResults />
          ) : (
            <>
              <div
                className={`properties ${
                  params.view === "grid" ? "grid" : "list"
                }`}
              >
                {properties.map((p) => (
                  <PropertyCard key={p.id} data={p} view={params.view} />
                ))}
              </div>

              <Pagination
                total={meta.total}
                current={meta.page}
                perPage={meta.perPage}
                onPageChange={changePage}
              />
            </>
          )}
        </main>

        {/* <div className={`plp-map ${mapOpen ? "open" : ""}`}>
          <MapView properties={properties} />
        </div> */}
      </div>
    </div>
  );
}
